import type {
  JHPBoardData,
  JHPContact,
  JHPLocation,
  JHPLocationFilterOptions,
  JHPLocationType,
  JHPUser,
  JHPWorkflowAttachment,
  JHPWorkflowEquipment,
  JHPWorkflowFilterOptions,
  JHPWorkflowItem,
  JHPWorkflowLocation,
} from 'jhp/types'
import mapboxgl from 'mapbox-gl'

import { LocationType, SitePermitStatus } from '~/client/graph'
import WeatherUnits from '~/client/src/shared/enums/WeatherUnits'
import WeatherForecastInfo from '~/client/src/shared/models/WeatherInfo'

import { getLocationParentNames } from '../components/ObjectReference/LocationReference'
import type {
  IJHPHourlyForecast,
  IJHPWeatherData,
  IJHPWeatherForecast,
  IJHPWeatherProjectAddress,
} from '../components/WeatherPresenters/types'
import { getFormsStatusAsTag } from '../constants/formStatusesTags'
import LocationBase from '../models/LocationObjects/LocationBase'
import { KnownLocationIntegrationMetadataKeys } from '../models/LocationObjects/LocationIntegration'
import Project from '../models/Project'
import User from '../models/User'
import UserProject from '../models/UserProject'
import Workflow from '../models/Workflow'
import ClosuresStore from '../stores/domain/Closures.store'
import CompaniesStore from '../stores/domain/Companies.store'
import {
  imageExtensions,
  pdfExtension,
} from '../stores/domain/FileUploading.store'
import LocationAttributesStore from '../stores/domain/LocationAttributes.store'
import OffloadingEquipmentsStore from '../stores/domain/OffloadingEquipments.store'
import ProjectMembersStore from '../stores/domain/ProjectMembers.store'
import ProjectRolesStore from '../stores/domain/ProjectRoles.store'
import type TagsStore from '../stores/domain/Tags.store'
import UserProjectsStore from '../stores/domain/UserProjects.store'
import WeatherForecastsStore from '../stores/domain/WeatherForecasts.store'
import WorkflowCategoriesStore from '../stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '../stores/domain/WorkflowTypes.store'
import ProjectDateStore from '../stores/ui/ProjectDate.store'
import IMsDateInterval from '../types/IMsDateInterval'
import {
  buildEffectiveOperatingIntervalsFmt,
  findNextOpeningFmt,
  resolveOperatingWindowForDateFmt,
} from '../utils/intervalFormatters'
import { isLocationOpen } from '../utils/locationOpenState'
import { isWeekdayScopedInterval } from '../utils/operatingIntervalMode'
import { getScheduleWorkingDays } from '../utils/projectSchedule'

export class JobsiteHomepageAdapter {
  public static transformUser(
    user: User,
    userProjectsStore: UserProjectsStore,
  ): JHPUser {
    const userProject = userProjectsStore.getByUser(user)

    return {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName,
      phoneNumber: user.phoneNumber,
      avatarUrl: user.avatarUrl,
      initials: user.initials,
      isAdmin: UserProject.getAdminStatusForFederation(user, userProjectsStore),
      accountPosition: user.accountPosition,
      roles: userProject?.roles || [],
      teams: userProject?.teams || [],
      trades: userProject?.trades || [],
      responsibilities: userProject?.responsibilities || [],
    }
  }

  // TODO: define state type; expand widget data sources as JHP grows.
  public static transformBoardData(
    project: Project,
    state?: any,
    stores?: {
      projectMembersStore?: ProjectMembersStore
      userProjectsStore?: UserProjectsStore
      companiesStore?: CompaniesStore
      workflowTypesStore?: WorkflowTypesStore
      workflowCategoriesStore?: WorkflowCategoriesStore
      locationAttributesStore?: LocationAttributesStore
      offloadingEquipmentsStore?: OffloadingEquipmentsStore
      projectRolesStore?: ProjectRolesStore
      closuresStore?: ClosuresStore
      projectDateStore?: ProjectDateStore
      weatherForecastsStore?: WeatherForecastsStore
      tagsStore?: TagsStore
    },
    /** Workflows fetched via GraphQL hook (not from store) */
    workflowPermits?: Workflow[],
  ): JHPBoardData {
    const formattedAddress = this.formatProjectAddress(state?.projectAddress)
    const hours = this.formatProjectHours(project)
    const generalContractors = this.findGeneralContractors(
      stores?.companiesStore,
    )

    return {
      project: {
        projectId: project.id,
        projectCode: project.code || '',
        name: project.name || '',
        address: formattedAddress,
        timezone: project.timezoneId || undefined,
        dateTimeFormat: project.dateTimeFormat,
        hours,
        generalContractors,
        headerImage: project.headerImageUrl || '',
        projectLogo: project.logoUrl || '',
        isLogoWhite: project.isLogoWhite,
      },

      map:
        state && stores?.locationAttributesStore
          ? this.transformMapData(state, stores.locationAttributesStore)
          : undefined,

      contacts: stores ? this.transformContacts(stores) : undefined,

      workflows: workflowPermits
        ? this.transformWorkflows(
            workflowPermits,
            stores?.workflowTypesStore,
            stores?.workflowCategoriesStore,
            stores?.companiesStore,
            stores?.projectMembersStore,
            stores?.locationAttributesStore,
            stores?.offloadingEquipmentsStore,
          )
        : undefined,
      workflowFilterOptions: workflowPermits
        ? this.getWorkflowFilterOptions(
            workflowPermits,
            stores?.workflowTypesStore,
            stores?.workflowCategoriesStore,
            stores?.companiesStore,
            stores?.locationAttributesStore,
            stores?.offloadingEquipmentsStore,
            stores?.projectMembersStore,
          )
        : undefined,

      locations: stores?.locationAttributesStore
        ? this.transformLocations(
            state,
            stores.locationAttributesStore,
            stores.closuresStore,
            project.timezoneId,
            stores.projectDateStore,
            stores.tagsStore,
          )
        : undefined,
      locationFilterOptions: stores?.locationAttributesStore
        ? this.getLocationFilterOptions(stores.locationAttributesStore)
        : undefined,

      weather: this.transformWeatherData(
        state?.projectAddress,
        stores?.weatherForecastsStore,
        stores?.projectDateStore,
      ),
    }
  }

  private static transformContacts(stores: {
    projectMembersStore?: ProjectMembersStore
    userProjectsStore?: UserProjectsStore
    companiesStore?: CompaniesStore
    projectRolesStore?: ProjectRolesStore
  }): JHPContact[] {
    const {
      projectMembersStore,
      userProjectsStore,
      companiesStore,
      projectRolesStore,
    } = stores

    if (!projectMembersStore || !userProjectsStore) {
      return []
    }

    const members = projectMembersStore.limitedList

    return members.map((user: User): JHPContact => {
      const companyId = UserProject.getCompanyId(user, userProjectsStore)
      const companyName = companyId
        ? companiesStore?.getCompanyNameById(companyId, '') || ''
        : ''

      const teamIds = UserProject.getTeamsIds(user, userProjectsStore)

      const roleIds = UserProject.getRolesIds(user, userProjectsStore)
      let roleName = ''
      if (roleIds.length > 0 && projectRolesStore) {
        const roleNames = roleIds
          .map(id => projectRolesStore.getInstanceById(id)?.name)
          .filter(Boolean)
        roleName = roleNames.join(', ')
      }

      return {
        id: user.id,
        fullName: User.getFullName(user),
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        avatarUrl: user.avatarUrl || '',
        initials:
          user.initials ||
          User.getInitialsToDisplay({
            initials: user.initials,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
          }),
        role: roleName,
        company: companyName,
        email: user.email || null,
        phoneNumber: user.phoneNumber || null,
        teams: teamIds,
        accountPosition:
          (user.accountPosition as 'field' | 'office' | 'management') ||
          'field',
      }
    })
  }

  private static findGeneralContractors(
    companiesStore?: CompaniesStore,
  ): Array<{ name: string; logo: string }> {
    if (!companiesStore) {
      return []
    }

    const gcTypeTag = companiesStore.getCompanyTypeTagByType(
      'GeneralContractorPrimary',
    )

    if (!gcTypeTag) {
      return []
    }

    const gcCompanies = companiesStore.availableSortedCompanies.filter(
      company => company.typeTags.includes(gcTypeTag.id),
    )

    return gcCompanies.map(company => ({
      name: company.name || '',
      logo: company.avatarUrl || company.logoUrl || '',
    }))
  }

  public static transformWorkflows(
    permits: Workflow[],
    workflowTypesStore?: WorkflowTypesStore,
    workflowCategoriesStore?: WorkflowCategoriesStore,
    companiesStore?: CompaniesStore,
    projectMembersStore?: ProjectMembersStore,
    locationAttributesStore?: LocationAttributesStore,
    offloadingEquipmentsStore?: OffloadingEquipmentsStore,
  ): JHPWorkflowItem[] {
    return permits.map((permit: Workflow): JHPWorkflowItem => {
      // getPermitTypeById finds any version of a permit type.
      const permitType = workflowTypesStore?.getPermitTypeById(permit.typeId)
      const typeLabel = permitType?.name || permit.typeId || 'Unknown'
      const iconName = permitType?.iconName || null

      // Stable type string is version-proof across permit-type revisions.
      const normalizedTypeId = permitType?.type || permit.typeId || null

      const currentTypeVersion = permitType?.type
        ? workflowTypesStore?.getActualTypeByType(permitType.type)
        : null
      const categoryId =
        currentTypeVersion?.categoryId || permitType?.categoryId || null
      const categoryLabel = categoryId
        ? workflowCategoriesStore?.getNameById(categoryId) || ''
        : ''

      const startDate = permit.startDate
        ? new Date(permit.startDate).toISOString()
        : null
      const endDate = permit.endDate
        ? new Date(permit.endDate).toISOString()
        : null

      const companyId = permit.companyIds?.[0] || null
      const company = companyId
        ? companiesStore?.getCompanyById(companyId)
        : null
      const companyName = company?.name || null
      // avatarUrl is square; logoUrl is full — fall back from square to full.
      const companyAvatarUrl = company?.avatarUrl || company?.logoUrl || null

      const responsibleContactId = permit.responsibleIds?.[0] || null
      const responsibleUser = responsibleContactId
        ? projectMembersStore?.getById(responsibleContactId)
        : null
      const responsibleContactName = responsibleUser
        ? User.getFullName(responsibleUser)
        : null
      const responsibleContactAvatarUrl = responsibleUser?.avatarUrl || null

      const locations: JHPWorkflowLocation[] = (
        permit.locationsWithEquipment || []
      )
        .map(loc => {
          const locationAttr = locationAttributesStore?.getById(loc.id)
          return {
            id: loc.id,
            name: locationAttr?.name || '',
            type: loc.type ? this.mapLocationType(loc.type) : 'building',
            color: locationAttr?.color || null,
            iconName: locationAttr?.iconName || null,
          }
        })
        .filter(loc => loc.name)

      const equipment: JHPWorkflowEquipment[] = (permit.equipment || [])
        .map(eq => {
          const equipmentItem = offloadingEquipmentsStore?.byId.get(eq.id)
          return {
            id: eq.id,
            name: equipmentItem?.name || '',
            color: equipmentItem?.color || null,
            iconName: equipmentItem?.iconName || 'equipment',
          }
        })
        .filter(eq => eq.name)

      const attachments: JHPWorkflowAttachment[] = (
        permit.attachments || []
      ).map(att => ({
        // IAttachment has no id field; url is unique.
        id: att.url,
        name: att.fileName,
        url: att.url,
        thumbnailUrl: this.getAttachmentThumbnailUrl(att.url, att.fileName),
        type: this.getAttachmentType(att.fileName),
      }))

      return {
        id: permit.id,
        name: permit.title || permit.code || 'Untitled',
        code: permit.code,
        typeId: normalizedTypeId,
        categoryId,
        status: permit.status,
        typeLabel,
        categoryLabel,
        workflowStepLevel: permit.workflowStepLevel,
        isLate: permit.isLate,
        startDate,
        endDate,
        iconName,
        companyId,
        companyName,
        companyAvatarUrl,
        responsibleContactId,
        responsibleContactName,
        responsibleContactAvatarUrl,
        isAllDay: permit.isAllDay,
        locations,
        equipment,
        attachments,
        hasAttachments: attachments.length > 0,
      }
    })
  }

  private static getAttachmentThumbnailUrl(
    url: string,
    fileName: string,
  ): string | null {
    const ext = fileName.split('.').pop()?.toLowerCase()
    return ext && imageExtensions.includes(ext) ? url : null
  }

  private static getAttachmentType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || ''

    if (imageExtensions.includes(ext)) return 'image'
    if (ext === pdfExtension) return 'pdf'

    if (['doc', 'docx', 'txt'].includes(ext)) return 'document'

    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'spreadsheet'

    return 'file'
  }

  public static getWorkflowFilterOptions(
    permits: Workflow[],
    workflowTypesStore?: WorkflowTypesStore,
    workflowCategoriesStore?: WorkflowCategoriesStore,
    companiesStore?: CompaniesStore,
    locationAttributesStore?: LocationAttributesStore,
    offloadingEquipmentsStore?: OffloadingEquipmentsStore,
    projectMembersStore?: ProjectMembersStore,
  ): JHPWorkflowFilterOptions {
    const statuses = Object.values(SitePermitStatus)
      .filter(s => s !== SitePermitStatus.Deleted)
      .map(status => {
        const tag = getFormsStatusAsTag(status)
        return {
          id: status,
          label: tag.name,
          color: tag.color,
        }
      })

    // Dedup by stable type string — matches backend + Zoomin initialTypes filtering.
    const typesByStableType = new Map<
      string,
      { id: string; label: string; iconName: string | null }
    >()
    workflowTypesStore?.actualTypes?.forEach(pt => {
      if (pt.type && !typesByStableType.has(pt.type)) {
        typesByStableType.set(pt.type, {
          id: pt.type,
          label: pt.name,
          iconName: pt.iconName || null,
        })
      }
    })
    // Supplement with permit-derived types not in actualTypes.
    permits.forEach(permit => {
      if (!permit.typeId) return
      const permitType = workflowTypesStore?.getPermitTypeById(permit.typeId)
      const stableType = permitType?.type
      if (!stableType || typesByStableType.has(stableType)) return
      typesByStableType.set(stableType, {
        id: stableType,
        label: permitType?.name || stableType,
        iconName: permitType?.iconName || null,
      })
    })
    const types = Array.from(typesByStableType.values())

    const categories =
      workflowCategoriesStore?.activeCategories?.map(cat => ({
        id: cat.id,
        label: cat.name,
      })) || []

    const companyIds = new Set<string>()
    permits.forEach(permit => {
      permit.companyIds?.forEach(id => companyIds.add(id))
    })
    const companies = Array.from(companyIds)
      .filter(id => id)
      .map(id => {
        const company = companiesStore?.getCompanyById(id)
        // Empty-string placeholder avoids the "[No company]" default.
        const name = companiesStore?.getCompanyNameById(id, '')
        return {
          id,
          label: name || id,
          avatarUrl: company?.avatarUrl || company?.logoUrl || null,
        }
      })
      .filter(c => c.label)

    const locationIds = new Set<string>()
    permits.forEach(permit => {
      permit.locations?.forEach(loc => {
        if (loc.id) {
          locationIds.add(loc.id)
        }
      })
    })
    const locations = Array.from(locationIds)
      .filter(id => id)
      .map(id => {
        const location = locationAttributesStore?.getById(id)
        return {
          id,
          label: location?.name || '',
          color: location?.color || null,
          iconName: location?.iconName || null,
        }
      })
      .filter(loc => loc.label)

    const equipmentIds = new Set<string>()
    permits.forEach(permit => {
      permit.equipment?.forEach(eq => {
        if (eq.id) {
          equipmentIds.add(eq.id)
        }
      })
    })
    const equipment = Array.from(equipmentIds)
      .filter(id => id)
      .map(id => {
        const equip = offloadingEquipmentsStore?.byId.get(id)
        return {
          id,
          label: equip?.name || '',
          color: equip?.color || null,
          iconName: equip?.iconName || 'equipment',
        }
      })
      .filter(eq => eq.label)

    const responsibleIds = new Set<string>()
    permits.forEach(permit => {
      permit.responsibleIds?.forEach(id => {
        if (id) {
          responsibleIds.add(id)
        }
      })
    })
    const responsibleContacts = Array.from(responsibleIds)
      .filter(id => id)
      .map(id => {
        const user = projectMembersStore?.getById(id)
        return {
          id,
          label: user ? User.getFullName(user) : '',
          avatarUrl: user?.avatarUrl || null,
          initials: user
            ? User.getInitialsToDisplay({
                initials: user.initials,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
              })
            : undefined,
        }
      })
      .filter(rc => rc.label)

    return {
      statuses,
      types,
      categories,
      companies,
      locations,
      equipment,
      responsibleContacts,
    }
  }

  public static transformLocations(
    state: any,
    locationAttributesStore: LocationAttributesStore,
    closuresStore?: ClosuresStore,
    timezoneId?: string,
    projectDateStore?: ProjectDateStore,
    tagsStore?: TagsStore,
  ): JHPLocation[] {
    // Locations don't store coordinates directly; they're on linked SitemapItem via assignedId.
    const locationCoords = new Map<
      string,
      { lat: number; lng: number } | null
    >()
    if (state?.sitemapItems) {
      Array.from(state.sitemapItems.values()).forEach((item: any) => {
        if (item.assignedId && item.coordinates) {
          locationCoords.set(item.assignedId, {
            lat: item.coordinates.latitude || item.coordinates.lat || 0,
            lng: item.coordinates.longitude || item.coordinates.lng || 0,
          })
        }
      })
    }

    const parentNameMap = new Map<string, string>()
    locationAttributesStore.allAttributes.forEach(loc => {
      if (loc.parent?.parentId) {
        const parentLoc = locationAttributesStore.getById(loc.parent.parentId)
        if (parentLoc) {
          parentNameMap.set(loc.id, parentLoc.name)
        }
      }
    })

    return locationAttributesStore.allAttributes
      .filter(loc => !loc.isDeleted)
      .map((loc: LocationBase): JHPLocation => {
        const coordinates = locationCoords.get(loc.id) || null

        const now = new Date()
        const locationClosures = closuresStore
          ? closuresStore.getLocationClosures(loc.id)
          : []
        const closureIntervals = locationClosures.map(c => c.closureInterval)

        // The hours the location is actually judged by. Inheritance is decided on
        // the RECURRING layer alone, the way the booking validator decides it: a
        // location stating only a dated override has said something about one date
        // and nothing about the rest of the week, so it still inherits the
        // project's — judging it by that lone window would shut it on every other
        // day. The dated windows are kept alongside, and the window builder gives
        // them precedence on the days they cover.
        // Collapsed on read, as the booking validator collapses: one recurring
        // window per day is the rule, but no migration enforces it on stored
        // records, and the API still accepts several every-day windows (only
        // duplicate weekdays and mixed modes are refused on write). Reading them
        // all would report the location open during a window the form has already
        // superseded - a lunch-break pair leaves the card open at 09:00 while the
        // form allows only the afternoon.
        //
        // Before the inheritance test below, not just before the window helpers:
        // whether the location states a recurring window at all has to be decided
        // on the same set everything else is judged by.
        const effectiveIntervals = projectDateStore
          ? buildEffectiveOperatingIntervalsFmt(
              projectDateStore,
              loc.operatingIntervals,
            )
          : []

        // Deliberately not isClosedBetween, which reads "no window covering now"
        // as unconstrained. That was right while a location either had operating
        // hours or had none; a weekly schedule adds a third state — has hours,
        // none today — where an empty result means shut. Closures are still its
        // business, so it judges those alone.
        //
        // No windows at all is shut too, not open: with inheritance the only way
        // to arrive there is a project that works no days, and the booking
        // validator refuses every booking against that same empty set. A card
        // reading "Open" beside a form that will not accept a booking is the
        // contradiction this whole path exists to avoid.
        const isOpen =
          projectDateStore && closuresStore
            ? isLocationOpen(loc, { projectDateStore, closuresStore })
            : undefined

        // Built from the hours that apply, so a location inheriting the project's
        // week reports that week rather than nothing — the card would otherwise
        // show a bare Open/Closed with no hours beside it, while the controls
        // chip advertises them.
        const hours = this.transformOperatingHours(
          effectiveIntervals,
          timezoneId,
          projectDateStore,
        )

        // Only where the card needs it. While the location is open the next
        // opening is noise, and it would have to be told apart from the closing
        // time it sits beside.
        const nextOpening =
          projectDateStore && isOpen === false && effectiveIntervals.length
            ? findNextOpeningFmt(
                projectDateStore,
                effectiveIntervals,
                now,
                closureIntervals,
              )
            : undefined

        const operatingHours =
          hours && nextOpening
            ? {
                ...hours,
                nextOpen: {
                  date: projectDateStore.getDashedFormattedDate(
                    nextOpening.startDate,
                  ),
                  open: this.formatOnProjectClock(
                    nextOpening.startDate,
                    timezoneId,
                    projectDateStore,
                  ),
                },
              }
            : hours

        // Left unsaid where the reopening is not today: the homepage words those
        // with the day ("Opens tomorrow", "Opens on Monday") from `nextOpen`, and
        // it takes this field in preference when set — so naming a bare time here
        // would suppress the better sentence and promise 8:00 AM on a day the
        // reader would assume is this one.
        const reopensToday =
          !!projectDateStore &&
          operatingHours?.nextOpen?.date ===
            projectDateStore.getDashedFormattedDate(now)

        let closureEndTime: string | undefined
        let nextClosureStartTime: string | undefined
        if (projectDateStore) {
          const nowMs = now.getTime()

          if (isOpen === false && locationClosures.length) {
            const activeClosure = locationClosures.find(c =>
              c.areWithinSelectedRange(
                projectDateStore,
                now,
                new Date(nowMs + 1),
              ),
            )
            if (activeClosure?.closureInterval?.endDate) {
              closureEndTime = this.formatOnProjectClock(
                activeClosure.closureInterval.endDate,
                timezoneId,
                projectDateStore,
              )
            }
          } else if (isOpen) {
            // Every candidate as an INSTANT, then the soonest of them. Both halves
            // used to go wrong on their own terms and then be compared on a third:
            //
            //  - a daily closure was reduced to the earliest time of day anyone
            //    had ever set, with no reference to now, so at 15:00 a pair at
            //    09:00 and 22:00 named 09:00 - a closure that had already run;
            //  - the two were then ranked by comparing "HH:mm" text, which reads
            //    02:00 as sooner than 22:00 though the 22:00 one comes first
            //    tonight.
            //
            // A daily closure carries only a time of day, so its next occurrence
            // is today at that time where that is still ahead, and tomorrow
            // otherwise. A dated one already is an instant.
            const nextDailyClosureStart = locationClosures
              .filter(c => c.closureInterval?.isDaily)
              .map(c => {
                const [hours, minutes] = projectDateStore.getHoursMinutes(
                  c.closureInterval.startDate,
                )
                const todayAt = projectDateStore
                  .setHours(now, hours, minutes)
                  .getTime()

                return todayAt > nowMs
                  ? todayAt
                  : projectDateStore.addDays(new Date(todayAt), 1).getTime()
              })
              .sort((a, b) => a - b)[0]

            const nextDatedClosureStart = locationClosures
              .filter(
                c =>
                  !c.closureInterval?.isDaily &&
                  c.closureInterval?.startDate > nowMs,
              )
              .map(c => c.closureInterval.startDate)
              .sort((a, b) => a - b)[0]

            // The hours shut it as surely as a closure does, so they are a
            // candidate too and the sooner of the two is what the card names. A
            // location working until 13:00 with a closure at 22:00 shuts at
            // 13:00; naming the closure put the doors closing nine hours late.
            //
            // Decided here rather than in the consumer, as the Graph decides it
            // in ComputeNextClosureStart. Both producers hold resolved instants;
            // a client given the formatted strings holds two clock faces and a
            // day, which cannot rank them across a DST transition - one where
            // 02:30 does not occur, or where 01:30 occurs twice.
            //
            // The window covering an open location is a merged stretch, so its
            // end is an absolute instant rather than a time-of-day carrier.
            const stretchEnd = resolveOperatingWindowForDateFmt(
              projectDateStore,
              effectiveIntervals,
              now,
            )?.endDate

            // Only where a shut gap actually follows it. A window's end is not a
            // closing unless the location is closed after it: a 24-hour daily
            // window ends at tomorrow's midnight and continues straight into the
            // next identical one, so naming that end told an always-open
            // location it shuts at 00:00. The same guard covers a split shift
            // whose next window begins the moment this one ends.
            const openUntil =
              stretchEnd != null &&
              !projectDateStore.isIntervalWithinOperatingWindows(
                stretchEnd,
                stretchEnd + 1,
                effectiveIntervals,
              )
                ? stretchEnd
                : undefined

            const nextStart = [
              nextDailyClosureStart,
              nextDatedClosureStart,
              openUntil,
            ]
              .filter(start => start != null && start > nowMs)
              .sort((a, b) => a - b)[0]

            // The project's clock, as the hours beside it are. formatTimestamp
            // falls back to UTC where a project states no timezone while the
            // store falls back to the viewer's, so these instants - resolved
            // through the store - would be named in a different zone from the
            // window they came from: hours reading 17:00 beside "Closes at
            // 10:00 PM" on the same card.
            if (nextStart != null) {
              nextClosureStartTime = this.formatOnProjectClock(
                nextStart,
                timezoneId,
                projectDateStore,
              )
            }
          }
        }

        // After the block above, which is where what shuts the location is
        // resolved. Deriving it from the window here instead gave the answer two
        // sources in one file, and they disagreed: a location open around the
        // clock has no closing, but its window still ends at tomorrow's midnight,
        // so this said "Closes at 12:00 AM" while nextClosureStartTime correctly
        // said nothing.
        const nextStatusChange =
          isOpen === false && operatingHours?.nextOpen && !reopensToday
            ? undefined
            : this.computeNextStatusChange(
                isOpen,
                operatingHours,
                nextClosureStartTime,
              )

        return {
          id: loc.id,
          name: loc.name,
          type: this.mapLocationType(loc.type),
          coordinates,
          description: '',
          code: loc.code,
          color: loc.color,
          iconName: loc.iconName || 'map-marker',
          operatingHours,
          isOpen,
          nextStatusChange,
          closureEndTime,
          nextClosureStartTime,
          parentName: parentNameMap.get(loc.id),
          resourceCode: loc.stxResourceId || undefined,
          parentNames: tagsStore
            ? getLocationParentNames(loc, tagsStore.tagStoreByTagTypeMap)
            : undefined,
        }
      })
  }

  public static getLocationFilterOptions(
    locationAttributesStore: LocationAttributesStore,
  ): JHPLocationFilterOptions {
    const typeCounts = new Map<LocationType, number>()
    locationAttributesStore.allAttributes
      .filter(loc => !loc.isDeleted)
      .forEach(loc => {
        const count = typeCounts.get(loc.type) || 0
        typeCounts.set(loc.type, count + 1)
      })

    const typeOrder: Array<{ type: LocationType; label: string }> = [
      { type: LocationType.Building, label: 'Building' },
      { type: LocationType.Zone, label: 'Zone' },
      { type: LocationType.Gate, label: 'Gate' },
      { type: LocationType.Route, label: 'Route' },
      { type: LocationType.Staging, label: 'Staging Area' },
      { type: LocationType.OffloadingEquipment, label: 'Equipment' },
      { type: LocationType.Area, label: 'Area' },
      { type: LocationType.Level, label: 'Level' },
      { type: LocationType.ProjectLocation, label: 'Project Location' },
      { type: LocationType.LogisticsObject, label: 'Logistics Object' },
      { type: LocationType.VerticalObject, label: 'Vertical Object' },
      { type: LocationType.InteriorDoor, label: 'Interior Door' },
      { type: LocationType.InteriorPath, label: 'Interior Path' },
    ]

    const types = typeOrder
      .filter(({ type }) => (typeCounts.get(type) || 0) > 0)
      .map(({ type, label }) => ({
        id: this.mapLocationType(type),
        label,
        count: typeCounts.get(type) || 0,
      }))

    return { types }
  }

  private static mapLocationType(type: LocationType): JHPLocationType {
    if (
      type === LocationType.Sensor ||
      type === LocationType.Material ||
      type === LocationType.Location
    ) {
      return 'building'
    }

    // LocationType.Location falls into the guard above rather than getting an arm: the
    // JHP wire vocabulary is location KINDS, and a collapsed location's kind lives in
    // data.type. Emitting it correctly is STX1-597 Stage 8 work, co-released with the
    // backend - until then no row carries this type.
    const typeMap: Record<
      Exclude<
        LocationType,
        LocationType.Sensor | LocationType.Material | LocationType.Location
      >,
      JHPLocationType
    > = {
      [LocationType.Building]: 'building',
      [LocationType.Zone]: 'zone',
      [LocationType.Gate]: 'gate',
      [LocationType.Staging]: 'staging',
      [LocationType.Route]: 'route',
      [LocationType.OffloadingEquipment]: 'equipment',
      [LocationType.Area]: 'area',
      [LocationType.Level]: 'level',
      [LocationType.InteriorDoor]: 'interiorDoor',
      [LocationType.InteriorPath]: 'interiorPath',
      [LocationType.LogisticsObject]: 'logisticsObject',
      [LocationType.VerticalObject]: 'verticalObject',
      [LocationType.ProjectLocation]: 'projectLocation',
      [LocationType.Integration]: 'logisticsObject',
    }
    return typeMap[type] || 'building'
  }

  private static formatTimestamp(
    timestamp: number,
    timezoneId?: string,
  ): string {
    try {
      const date = new Date(timestamp)
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneId || 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      return formatter.format(date)
    } catch {
      return ''
    }
  }

  /**
   * The hours the card shows: today's window, plus the week behind it.
   *
   * Read the first stored interval and a resource on a weekly schedule reports
   * whichever weekday happens to sort first — Sunday's hours on a Tuesday. The
   * window is resolved for today instead, by the same precedence the booking
   * validator applies, and the week is published alongside so the card can show
   * it rather than infer one.
   */
  /**
   * A time of day on the PROJECT's clock.
   *
   * `formatTimestamp` falls back to UTC where the project states no timezone
   * while the store falls back to the viewer's, so a project without one would
   * have its hours formatted in a different zone from the day they were resolved
   * for. The store is the project's clock everywhere else in the app.
   */
  private static formatOnProjectClock(
    timestamp: number,
    timezoneId?: string,
    projectDateStore?: ProjectDateStore,
  ): string {
    if (!projectDateStore) return this.formatTimestamp(timestamp, timezoneId)

    const [hours, minutes] = projectDateStore.getHoursMinutes(timestamp)

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0',
    )}`
  }

  private static transformOperatingHours(
    operatingIntervals: IMsDateInterval[],
    timezoneId?: string,
    projectDateStore?: ProjectDateStore,
  ): JHPLocation['operatingHours'] {
    if (!operatingIntervals?.length) {
      return undefined
    }

    const formatTime = (ts: number): string =>
      this.formatOnProjectClock(ts, timezoneId, projectDateStore)

    const windowOf = (
      interval: IMsDateInterval | undefined,
    ): { open: string; close: string } | undefined => {
      if (!interval?.startDate || !interval?.endDate) return undefined

      const open = formatTime(interval.startDate)
      const close = formatTime(interval.endDate)

      return open && close ? { open, close } : undefined
    }

    // Without the store there is no project clock to ask which day it is, so the
    // pre-weekday reading is kept rather than guessing on the viewer's.
    if (!projectDateStore) {
      return windowOf(operatingIntervals[0])
    }

    const today = windowOf(
      resolveOperatingWindowForDateFmt(
        projectDateStore,
        operatingIntervals,
        new Date(),
      ),
    )

    const weekdayScoped = operatingIntervals.filter(isWeekdayScopedInterval)

    const schedule = weekdayScoped.length
      ? weekdayScoped
          .map(interval => ({
            // The Graph publishes this axis 0-based to JHP while its own
            // entities are 1-based; match what the standalone app already reads.
            dayOfWeek: interval.dayOfWeek - 1,
            ...windowOf(interval),
          }))
          .filter(
            (day): day is { dayOfWeek: number; open: string; close: string } =>
              !!day.open && !!day.close,
          )
          .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
      : undefined

    const extras = schedule?.length ? { schedule } : {}

    if (!today) {
      // States hours, none of them today: shut, and distinct from stating none.
      // The times are absent rather than blank — a blank reads as a time to
      // everything downstream, which rendered "Opens at " with nothing after it.
      return { open: null, close: null, isClosedToday: true, ...extras }
    }

    return { ...today, ...extras }
  }

  private static computeNextStatusChange(
    isOpen: boolean | undefined,
    operatingHours: JHPLocation['operatingHours'],
    nextClosureStartTime?: string,
  ): string | undefined {
    if (isOpen === undefined || !operatingHours) {
      return undefined
    }

    // A day the resource never opens has no time to name here. Saying "Opens at"
    // with nothing after it is worse than saying nothing; the card states the
    // closure from `isClosedToday`, and `nextOpen` when it can say more.
    //
    // Closed, the reopening is what `nextOpen` resolved, not the window's start:
    // those differ whenever a closure runs into the window, and naming the start
    // would promise a time the site is still shut.
    // Open, what shuts it is whatever the closure pass resolved - the sooner of
    // its hours and its next closure, or nothing at all where neither applies.
    // The window's own close is not that answer: it ends at tomorrow's midnight
    // for a location open around the clock, which never shuts.
    const time = isOpen
      ? nextClosureStartTime
      : (operatingHours.nextOpen?.open ?? operatingHours.open)
    if (!time) {
      return undefined
    }

    const formatTimeForDisplay = (time: string): string => {
      try {
        const [hours, minutes] = time.split(':').map(Number)
        const period = hours >= 12 ? 'PM' : 'AM'
        const displayHours = hours % 12 || 12
        return `${displayHours}:${minutes
          .toString()
          .padStart(2, '0')} ${period}`
      } catch {
        return time
      }
    }

    return isOpen
      ? `Closes at ${formatTimeForDisplay(time)}`
      : `Opens at ${formatTimeForDisplay(time)}`
  }

  // TODO: define state type.
  // Public for unit tests; not part of the adapter contract.
  public static transformMapData(
    state: any,
    locationAttributesStore: LocationAttributesStore,
  ): any {
    const globeViews = Array.from(state.globeViews.values()).map((globe: any) =>
      this.serializeGlobeView(globe),
    )
    const sitemaps = Array.from(state.sitemaps.values()).map((sitemap: any) =>
      this.serializeSitemap(sitemap),
    )
    const basemaps = Array.from(state.basemaps.values()).map((basemap: any) =>
      this.serializeBasemap(basemap),
    )

    // Use each store's `.list` getter — it filters soft-deleted entries,
    // which JHP must not see (orphan markers via linked SitemapItems).
    const buildings = locationAttributesStore.buildingsStore.list
    const zones = locationAttributesStore.zonesStore.list
    const gates = locationAttributesStore.gatesStore.list
    const routes = locationAttributesStore.routesStore.list
    const projectLocations = locationAttributesStore.projectLocationsStore.list
    const logisticsObjects = locationAttributesStore.logisticsObjectsStore.list
    const stagings = locationAttributesStore.stagingsStore.list
    const offloadingEquipments =
      locationAttributesStore.offloadingEquipmentsStore.list
    const levels = locationAttributesStore.levelsStore.list
    const areas = locationAttributesStore.areasStore.list
    const interiorDoors = locationAttributesStore.interiorDoorsStore.list
    const interiorPaths = locationAttributesStore.interiorPathsStore.list
    const verticalObjects = locationAttributesStore.verticalObjectsStore.list
    const locationIntegrations =
      locationAttributesStore.locationIntegrationsStore.list
    const otherLocations = locationAttributesStore.otherLocationsStore.list

    const activeLocationIds = new Set(
      locationAttributesStore.allAttributes.map(loc => loc.id),
    )

    // Drop items whose assignedId points to a deleted or unknown location
    // — keeping them would leave orphan markers on JHP maps.
    const sitemapItems = Array.from(state.sitemapItems.values())
      .filter(
        (item: any) =>
          !item.assignedId || activeLocationIds.has(item.assignedId),
      )
      .map((item: any) => this.serializeSitemapItem(item))

    const locations = {
      buildings: buildings.map(b => this.serializeLocation(b)),
      zones: zones.map(z => this.serializeLocation(z)),
      gates: gates.map(g => this.serializeLocation(g)),
      routes: routes.map(r => this.serializeLocation(r)),
      projectLocations: projectLocations.map(pl => this.serializeLocation(pl)),
      logisticsObjects: logisticsObjects.map(lo => this.serializeLocation(lo)),
      stagings: stagings.map(s => this.serializeLocation(s)),
      offloadingEquipments: offloadingEquipments.map(oe =>
        this.serializeLocation(oe),
      ),
      levels: levels.map(l => this.serializeLocation(l)),
      areas: areas.map(a => this.serializeLocation(a)),
      interiorDoors: interiorDoors.map(id => this.serializeLocation(id)),
      interiorPaths: interiorPaths.map(ip => this.serializeLocation(ip)),
      verticalObjects: verticalObjects.map(vo => this.serializeLocation(vo)),
      // Integration-assigned items survive the orphan filter above, so their locations have to ship
      // too. The type rides along because a Maturix station hides its label by default.
      locationIntegrations: locationIntegrations.map(li => ({
        ...this.serializeLocation(li),
        metadata: [
          {
            key: KnownLocationIntegrationMetadataKeys.Type,
            value: li.integrationType,
          },
        ],
      })),
      // The catch-all bucket ships too: allAttributes holds it, so its markers survive the
      // orphan filter above and need their locations behind them (STX1-597).
      otherLocations: otherLocations.map(o => this.serializeLocation(o)),
    }

    const tilesets = Array.from(state.tilesets.values()).map((tileset: any) =>
      this.serializeTileset(tileset),
    )

    const remoteTilesets = Array.from(state.remoteTilesets.values()).map(
      (remoteTileset: any) => this.serializeRemoteTileset(remoteTileset),
    )

    // Maps marked for "Home" display.
    const logisticsAssignedGlobes = (state.logisticsMapIdsList || [])
      .filter((m: any) => m.globeViewId)
      .reduce((acc: any, m: any) => {
        acc[m.globeViewId] = true
        return acc
      }, {})

    const logisticsAssignedSitemaps = (state.logisticsMapIdsList || [])
      .filter((m: any) => m.sitemapId)
      .reduce((acc: any, m: any) => {
        acc[m.sitemapId] = true
        return acc
      }, {})

    // InitialState has no `address` — it is projectAddress.center, so embedded JHP shipped none.
    const projectAddress = state.projectAddress?.center
      ? {
          center: {
            lat: state.projectAddress.center.lat,
            lng: state.projectAddress.center.lng,
          },
          // As saved, undefined included: the viewer reads only `center` from here.
          zoom: state.projectAddress.zoom,
        }
      : undefined

    return {
      mapboxAccessToken: mapboxgl.accessToken as string,
      projectAddress,
      logisticsAssignedGlobes,
      logisticsAssignedSitemaps,
      globeViews,
      sitemaps,
      basemaps,
      sitemapItems,
      locations,
      tilesets,
      remoteTilesets,
    }
  }

  private static serializeGlobeView(globe: any): any {
    return {
      id: globe.id,
      name: globe.name,
      projectId: globe.projectId,
      center: {
        lat: globe.center?.lat || 0,
        lng: globe.center?.lng || 0,
      },
      // Bounds drive capability === 'georeferenced' in the JHP MapViewsPicker.
      // Keep nullable so non-georeferenced globes round-trip without fake coordinates.
      bounds: globe.bounds,
      zoom: globe.zoom || 13,
      pitch: globe.pitch || 0,
      bearing: globe.bearing || 0,
      mapType: globe.style?.mapType || 'Streets',
      filledImage: globe.filledImage,
      items: globe.items || [],
      style: globe.style,
      mapFiles: globe.mapFiles || [],
    }
  }

  private static serializeSitemap(sitemap: any): any {
    return {
      id: sitemap.id,
      name: sitemap.name,
      projectId: sitemap.projectId,
      basemapId: sitemap.basemapId,
      center: sitemap.center
        ? {
            lat: sitemap.center.lat,
            lng: sitemap.center.lng,
          }
        : undefined,
      zoom: sitemap.zoom,
      isReferenced: sitemap.isReferenced,
      geoCorners: sitemap.geoCorners,
      filledImage: sitemap.filledImage,
      items: sitemap.items || [],
    }
  }

  private static serializeBasemap(basemap: any): any {
    return {
      id: basemap.id,
      name: basemap.name,
      source: basemap.source,
      projectId: basemap.projectId,
    }
  }

  private static serializeLocation(location: any): any {
    return {
      id: location.id,
      name: location.name,
      projectId: location.projectId,
      type: location.type,
      color: location.color,
      iconName: location.iconName,
      code: location.code,
      parent: location.parent,
      // JHP MapViewsPicker scope row reads map-view "owners" from these arrays;
      // without them every card renders the bare-project scope.
      assignedSitemaps: location.assignedSitemaps || [],
      assignedGlobes: location.assignedGlobes || [],
      // Read by the shared item builder. fromDto defaults isLinked to TRUE, so omitting it
      // would re-anchor project locations onto the address.
      isLinked: location.isLinked,
      effectivePosition: location.effectivePosition,
    }
  }

  private static serializeSitemapItem(item: any): any {
    return {
      id: item.id,
      name: item.name,
      color: item.color,
      type: item.type,
      iconName: item.iconName,
      projectId: item.projectId,
      // assignedTo is no longer sent or selected (STX1-1355); JobsiteHomepage.web types
      // sitemapItems as any[] and never reads it.
      assignedId: item.assignedId,
      parent: item.parent,
      coordinates: item.coordinates,
      shapeCoordinates: item.shapeCoordinates,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    }
  }

  private static serializeTileset(tileset: any): any {
    return {
      id: tileset.id,
      name: tileset.name,
      projectId: tileset.projectId,
      mapboxTilesetId: tileset.mapboxTilesetId,
      isProcessed: tileset.isProcessed,
      status: tileset.status,
      mapboxData: tileset.mapboxData
        ? {
            id: tileset.mapboxData.id,
            type: tileset.mapboxData.type,
            name: tileset.mapboxData.name,
            center: tileset.mapboxData.center,
            fileSize: tileset.mapboxData.fileSize,
          }
        : undefined,
      createdAt: tileset.createdAt,
      updatedAt: tileset.updatedAt,
    }
  }

  private static serializeRemoteTileset(remoteTileset: any): any {
    return {
      id: remoteTileset.id,
      name: remoteTileset.name,
      projectId: remoteTileset.projectId,
      source: remoteTileset.source,
      createdAt: remoteTileset.createdAt,
      updatedAt: remoteTileset.updatedAt,
    }
  }

  public static async getWidgetConfiguration(
    configStore: any, // Type: JobsiteHomepageConfigurationStore
  ): Promise<any> {
    try {
      await configStore.load()
      return configStore.parsedConfiguration
    } catch (error) {
      console.error('Failed to get JHP configuration:', error)
      return configStore.defaultConfiguration || { widgets: [] }
    }
  }

  public static async saveWidgetConfiguration(
    projectId: string,
    userId: string,
    widgetConfig: any[],
    configStore: any, // Type: JobsiteHomepageConfigurationStore
    settings?: any, // Board-level settings (header image offset, etc.)
  ): Promise<void> {
    try {
      await configStore.save(widgetConfig, settings)
    } catch (error) {
      console.error('Failed to save JHP configuration:', error)
      throw error
    }
  }

  private static formatProjectAddress(projectAddress: any): string {
    if (!projectAddress) return ''

    const parts = [
      projectAddress.address,
      projectAddress.city,
      projectAddress.state,
      projectAddress.zipcode,
      projectAddress.country,
    ].filter(Boolean)

    return parts.join(', ')
  }

  private static formatProjectHours(project: Project): any {
    const timezone = project.timezoneId || 'UTC'

    const formatTime = (timestamp: number | undefined): string => {
      if (!timestamp) return ''
      try {
        const date = new Date(timestamp)
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        // Some browsers render midnight as 24:00 — the same quirk
        // ProjectDateStore.getDateInTimezone guards. It matters more now that a
        // 24-hour day makes midnight a real boundary.
        return formatter.format(date).replace(/^24:/, '00:')
      } catch {
        return ''
      }
    }

    // Project uses 1-indexed (1=Sunday); convert to 0-indexed. Off
    // `workingSchedule`, the source of truth — the server sends the effective
    // schedule, so a project that never stored one still arrives with its legacy
    // days expanded into rows and neither legacy field is read (STX1-983).
    const workingDays = getScheduleWorkingDays(project.workingSchedule).map(
      d => d - 1,
    )

    // Each worked weekday's own window. `workingHours` above is only the range
    // the most days share and may match no single day, so anything reading this
    // per day needs the breakdown — the same shape, on the same 0-based axis,
    // that the Graph sends JHP in standalone mode (STX1-983).
    const schedule = (project.workingSchedule || [])
      .map(day => ({
        dayOfWeek: day?.dayOfWeek - 1,
        open: formatTime(day?.startTime),
        close: formatTime(day?.endTime),
      }))
      // A row that is null, or missing either time, has no window to publish —
      // and reading through it has already turned both into empty strings. The
      // schema types the rows as nullable, so one may arrive (STX1-983).
      .filter(day => !!day.open && !!day.close)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)

    const closures = (project.closedIntervals || []).map(interval => ({
      startDate: interval.startDate
        ? new Date(interval.startDate).toISOString()
        : '',
      endDate: interval.endDate ? new Date(interval.endDate).toISOString() : '',
    }))

    // The range the most days share, taken from the rows above so it is measured
    // on the project's clock like they are — the same thing the legacy
    // `workingHours` held, and the same rule the Graph derives it by. Ties go to
    // the range worked on the earliest weekday: `schedule` is sorted by day, but
    // resolving the tie on the day rather than on position keeps the answer a
    // function of the schedule either way.
    const rangeCounts = new Map<string, number>()
    schedule.forEach(({ open, close }) => {
      const key = `${open}-${close}`
      rangeCounts.set(key, (rangeCounts.get(key) || 0) + 1)
    })

    const [representative] = [...schedule].sort(
      (a, b) =>
        rangeCounts.get(`${b.open}-${b.close}`) -
          rangeCounts.get(`${a.open}-${a.close}`) || a.dayOfWeek - b.dayOfWeek,
    )

    return {
      open: representative?.open || '07:00',
      close: representative?.close || '17:00',
      timezone,
      dateTimeFormat: project.dateTimeFormat,
      workingDays,
      // Omitted rather than empty, so a consumer's "is there a schedule?" test
      // stays a simple presence check.
      ...(schedule.length ? { schedule } : {}),
      closures,
    }
  }

  private static transformWeatherData(
    projectAddress: any,
    weatherForecastsStore?: WeatherForecastsStore,
    projectDateStore?: ProjectDateStore,
  ): IJHPWeatherData | undefined {
    if (
      !weatherForecastsStore ||
      weatherForecastsStore.weatherByDaysList.length === 0
    ) {
      return undefined
    }

    const timezone = projectDateStore?.timezoneId || 'UTC'

    const forecasts = this.transformWeatherForecasts(weatherForecastsStore)

    const address: IJHPWeatherProjectAddress = {
      city: projectAddress?.city,
      state: projectAddress?.state,
      country: projectAddress?.country,
    }

    const units =
      weatherForecastsStore.weatherUnits === WeatherUnits.Imperial
        ? 'imperial'
        : 'metric'

    return {
      forecasts,
      projectAddress: address,
      timezone,
      units,
    }
  }

  private static transformWeatherForecasts(
    weatherForecastsStore: WeatherForecastsStore,
  ): IJHPWeatherForecast[] {
    const dailyForecasts = weatherForecastsStore.weatherByDaysList

    return dailyForecasts.map(
      (forecast: WeatherForecastInfo): IJHPWeatherForecast => {
        let hourlyForecasts: IJHPHourlyForecast[] | undefined
        try {
          const hourlyData = weatherForecastsStore.getHourlyForecastForDay(
            forecast.date,
          )
          if (hourlyData?.length > 0) {
            hourlyForecasts = hourlyData.map(
              (hourly: WeatherForecastInfo): IJHPHourlyForecast => ({
                time: hourly.date,
                temperature: hourly.currentTemp,
                weatherIconCode: hourly.weatherIconCode || '01d',
                probabilityOfPrecipitation: hourly.probabilityOfPrecipitation,
                windSpeed: hourly.windSpeed,
                windDegree: hourly.windDegree,
                amountOfPrecipitation: hourly.amountOfPrecipitation,
              }),
            )
          }
        } catch {
          hourlyForecasts = undefined
        }

        return {
          date: forecast.date,
          temperature: forecast.currentTemp,
          minTemperature: forecast.minTemp,
          maxTemperature: forecast.maxTemp,
          humidity: forecast.humidity,
          windSpeed: forecast.windSpeed,
          windDegree: forecast.windDegree,
          probabilityOfPrecipitation: forecast.probabilityOfPrecipitation,
          amountOfPrecipitation: forecast.amountOfPrecipitation,
          weatherIconCode: forecast.weatherIconCode || '01d',
          weatherDescription: forecast.weatherDescription || '',
          hourlyForecasts,
        }
      },
    )
  }
}
